import { CheckboxField, Col, SelectField } from '@/components';
import type { ProjectSearchFormType } from '@/features/search/_models/projects-search';
import {
  shouldShowHasOnlyQualitativeIndicator,
  shouldShowIndicatorDataNatureType,
  shouldShowIndicatorTerm,
} from '@/features/search/_utils/activity-indicator-conditions';
import { controlActivityIndicatorByType } from '@/features/search/_utils/activity-indicator-control';
import {
  IndicatorTypeModifier,
  indicatorTypeEnum,
} from '@/features/search/_utils/enum';
import { COL_MIN_WIDTH } from '@/features/search/_utils/projects-search';
import { isPS } from '@/utils';
import type { FC } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type z from 'zod';

export const ActivityIndicatorTypeColField: FC = () => {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext<ProjectSearchFormType>();
  const indicatorType = useWatch({ control, name: 'indicatorType' });
  const hasOnlyQualitativeIndicatorType =
    shouldShowHasOnlyQualitativeIndicator(indicatorType);

  return (
    <Col span={4} style={{ minWidth: COL_MIN_WIDTH.large }}>
      <SelectField
        name="indicatorType"
        label={t('item')}
        control={control}
        errors={errors}
        hint={!isPS() ? t('hint-search-activity-indicator-type') : undefined}
        options={indicatorTypeEnum.options.map((value) => ({
          value,
          label: IndicatorTypeModifier[value],
        }))}
        shouldCoexistOnChange
        onChange={(v) => {
          const value = v as z.infer<typeof indicatorTypeEnum> | undefined;
          controlActivityIndicatorByType({
            setValue,
            value: value ?? 'reset',
          });
        }}
        additionalLabelComponent={
          hasOnlyQualitativeIndicatorType.result === 'unNeed' ? null : (
            <CheckboxField
              text={t('search-has-only-qualitative-indicator')}
              name="hasOnlyQualitativeIndicator"
              control={control}
              errors={errors}
              shouldCoexistOnChange
              onChange={(e) => {
                const checked = e.target.checked;
                if (!shouldShowIndicatorTerm(indicatorType, checked)) {
                  setValue('indicatorTerm', null);
                }
                if (
                  shouldShowIndicatorDataNatureType(indicatorType, checked)
                    .result !== 'enable'
                ) {
                  setValue('indicatorDataNatureType', null);
                }
              }}
              disabled={hasOnlyQualitativeIndicatorType.result === 'disable'}
              wrapperStyle={{
                marginBottom: 0,
                marginLeft: 4,
                height: '22px',
                // .ant-form-item-control-inputのmin-heightを上書きするのが難しいので無理やり位置調整してます。
                transform: 'translateY(-4px)',
              }}
            />
          )
        }
      />
    </Col>
  );
};
